/**
 * Suggested filename for a batch export.
 *   `receipts-YYYY-MM-DD.<extension>`
 * Today's date — users typically download "what I just exported", not a
 * filename pegged to any specific receipt's date.
 */
export function batchFilename(extension: string): string {
  const today = new Date().toISOString().slice(0, 10);
  return `receipts-${today}.${extension}`;
}

/**
 * Build a per-receipt filename `YYYY-MM-DD_vendor` from a parsed receipt.
 * Date-first so files sort chronologically by default in any file manager
 * (alphabetical sort on ISO dates is also chronological).
 *
 * Sanitisation rules for the vendor portion:
 *   - non-alphanumerics (other than `-`) → `_`
 *   - consecutive underscores collapsed
 *   - leading/trailing underscores stripped
 *   - vendor truncated to 50 chars to leave room for the date + extension
 *     while staying under common filesystem limits
 *
 * Returns the stem only (no extension) — the caller appends `.jpg` etc.
 * The caller is also responsible for collision-suffixing when two receipts
 * produce the same stem; see `uniqueStem`.
 */
export function receiptFilenameStem(vendor: string, date: Date): string {
  const slug = vendor
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}-]+/gu, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
  const safeVendor = slug.length === 0 ? 'receipt' : slug;
  const isoDay = date.toISOString().slice(0, 10);
  return `${isoDay}_${safeVendor}`;
}

/**
 * Append `_2`, `_3`, ... to a filename stem when a previous receipt already
 * claimed the same one in this batch. Mutates the provided `seen` map so
 * callers can call repeatedly across a batch.
 */
export function uniqueStem(stem: string, seen: Map<string, number>): string {
  const count = seen.get(stem) ?? 0;
  seen.set(stem, count + 1);
  return count === 0 ? stem : `${stem}_${count + 1}`;
}
